import React, { useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainLayout from "../../../../layouts/MainLayout";
import PageHeader from "../../../../components/PageHeader";
import "../../../../assets/styles/custom-styles.css";
import { removeMask } from "../../../../utils/maskUtils";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import useForm from "../../../../hooks/useForm";
import { editAddressFields } from "../../../../constants/forms/addressFields";
import Form from "../../../../components/forms/Form";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const EditCustomerAddressPage = () => {
  const navigate = useNavigate();
  const { id, addressId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const {
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, setFormData, handleChange } = useForm(
    setDefaultFieldValues(editAddressFields),
  );

  // --- Lógica para buscar CEP automaticamente ---
  const fetchCepData = useCallback(
    async (cepValue) => {
      const unmaskedCep = removeMask(cepValue);
      if (unmaskedCep.length === 8) {
        showLoader();
        try {
          const response = await fetch(
            `https://viacep.com.br/ws/${unmaskedCep}/json/`,
          );
          if (!response.ok) throw new Error("Erro ao buscar o CEP");

          const data = await response.json();
          if (data.erro) throw new Error("CEP não encontrado");

          setFormData((prev) => ({
            address: {
              ...prev.address,
              street: data.logradouro || "",
              district: data.bairro || "",
              city: data.localidade || "",
              state: data.estado || "",
              country: "Brasil",
            },
          }));
          showNotification("success", "Endereço preenchido automaticamente!");
        } catch (error) {
          showNotification("error", error.message);
        } finally {
          hideLoader();
        }
      }
    },
    [setFormData, showLoader, hideLoader, showNotification],
  );

  // --- Query para buscar os detalhes do Endereço ---
  const { data: addressData, isLoading: isLoadingAddress } = useQueryWithLoader(
    {
      queryKey: ["customerAddress", id, addressId],
      queryFn: async () => {
        showLoader();
        const response = await baseServiceGetByColumn(
          entities.customers.addresses.getById(id, addressId),
        );
        return response.result;
      },
      enabled: !!id && !!addressId,
      staleTime: 5 * 60 * 1000,
    },
  );

  useEffect(() => {
    // --- Lógica para buscar CEP automaticamente ---
    if (
      addressData?.zip !== formData?.address?.zip &&
      removeMask(formData?.address?.zip).length === 8
    ) {
      fetchCepData(formData?.address?.zip);
    }
  }, [formData.address.zip]);

  useEffect(() => {
    if (addressData) {
      setFormData({
        address: addressData,
      });
    }
  }, [addressData, setFormData]);

  // --- Mutação para atualizar o Endereço ---
  const updateAddressMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePut(
        entities.customers.addresses.update(id, addressId),
        dataToUpdate,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["customerAddress", id, addressId],
        });
        queryClient.invalidateQueries({ queryKey: ["customerAddresses", id] });
        navigate(`/aluguel/clientes/detalhes/${id}`, {
          state: { type: "success" },
        });
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const sanitizedData = {
        ...cleanedData,
        address: {
          ...cleanedData.address,
          addressable_type: "customer",
          number: cleanedData.address.number
            ? String(cleanedData.address.number)
            : null,
          zip: cleanedData.address.zip
            ? removeMask(cleanedData.address.zip)
            : "",
        },
      };
      await updateAddressMutation.mutateAsync(sanitizedData);
    } catch (error) {
      console.error("Erro ao atualizar endereço:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateAddressMutation]);

  const handleBack = useCallback(() => {
    navigate(`/aluguel/clientes/detalhes/${id}`);
  }, [id, navigate]);

  // --- Efeito para exibir notificações de estado da rota ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  // --- Renderização do formulário ---
  const renderForm = useCallback(() => {
    const renderedSections = new Set();

    return editAddressFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={() => {}}
            getSelectedValue={() => {}}
          />
        </div>
      );
    });
  }, [formData, formErrors, handleChange]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Edição de Endereço do Cliente"
        showBackButton={true}
        backUrl={`/aluguel/clientes/detalhes/${id}`}
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Salvar Alterações"
          textLoadingSubmit="Salvando..."
          handleBack={handleBack}
          isLoading={isLoadingAddress || updateAddressMutation.isPending}>
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditCustomerAddressPage;
